import React from "react";
import { useNavigate } from "react-router-dom";
import "./invitationform.scss";
import Input from "../../common/FormInputs";
import { useFormik } from "formik";
import * as Yup from "yup";
import { sendAdminInvite } from "../../../services/onboardingService";
import { getMerchants } from "../../../services/merchantService";
import { useDispatch, useSelector } from "react-redux";
import {
  FaLinkedinIn,
  FaTiktok,
  FaInstagram,
  FaFacebookF,
  FaYoutube,
} from "react-icons/fa";
import InviteMerchantInfographic from "../../../Assets/Icons/inviteamerhcantinfographic.svg";
import InviteMerchantInfographicMobile from "../../../Assets/Icons/merchantinfographicmobile.svg";
import EnvelopeIcon from "../../../Assets/Icons/envelopeicon.svg";
import Loader from "../../common/Loader";
import toast from "../../../utils/toast";
import axiosInstance from "../../../utils/axios";
import { ReactComponent as InfoIcon } from "../../../Assets/Icons/info.svg";
import { ReactComponent as UploadIcon } from "../../../Assets/Icons/csvupload.svg";
import { ReactComponent as CsvIcon } from "../../../Assets/Icons/csvIcon.svg";
import { ReactComponent as DownloadIcon } from "../../../Assets/Icons/downloadIcon.svg";
import { ReactComponent as SwoodleLogo } from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import { Tooltip } from "@mui/material";
import {
  X,
  Eye,
  Trash2,
  Check,
  Pencil,
  Monitor,
  Smartphone,
  Type,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DetailsModal from "../../common/DetailsModal";
import PhoneFormInput from "../../common/PhoneInput";
import { inferCountryFromPhoneNumber } from "../../../utils/phoneValidation";

const DEFAULT_INVITE_TEMPLATE = `
<div style="font-family: 'Poppins', sans-serif; color: #535353;">
  <p style="font-size: 14px; line-height: 1.6; margin-bottom: 12px;">
    Hi there,
  </p>
  <p style="font-size: 14px; line-height: 1.6; margin-bottom: 12px;">
    You've been invited by the Swoodle team to join <strong>Swoodle</strong>, a platform that helps businesses grow by supporting community fundraisers.
  </p>
  <p style="font-size: 14px; line-height: 1.6; margin-bottom: 16px;">
    To get started, simply click the button below and create your merchant account by sharing a few details about your business. Once signed up, you'll have access to your <strong>Merchant Dashboard</strong>, where you can create offers, track performance, and connect with local customers.
  </p>
</div>
`;

const InvitaionFormPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.onboardingReducer?.loading);
  const [csvFile, setCsvFile] = React.useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [editedEmail, setEditedEmail] = React.useState("");
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [emailStatus, setEmailStatus] = React.useState({});
  const [customEmailStatus, setCustomEmailStatus] = React.useState(null);
  const [isSearchLoading, setIsSearchLoading] = React.useState(false);

  const [showCustomEmail, setShowCustomEmail] = React.useState(false);
  const [emailSubject, setEmailSubject] = React.useState(
    "You're Invited to Join Swoodle!",
  );
  const [emailBody, setEmailBody] = React.useState(DEFAULT_INVITE_TEMPLATE);
  const [previewMode, setPreviewMode] = React.useState("desktop");
  const [allInvites, setAllInvites] = React.useState([]);

  const normalizeStatus = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const classifyAccountStatus = React.useCallback((entity, record) => {
    const status = normalizeStatus(record?.status);
    const isDeleted = record?.isDeleted === true || status === "deleted";
    const isDisabled =
      record?.isDisabled === true ||
      ["disabled", "declined", "rejected", "inactive"].includes(status);
    const isLive = [
      "live",
      "approved",
      "active",
      "accepted",
      "completed",
      "joined",
    ].includes(status);
    const isPending = [
      "pending",
      "in-process",
      "in process",
      "in-progress",
      "in progress",
      "in_progress",
    ].includes(status);

    if (isDeleted) return `${entity}_deleted`;
    if (isDisabled) return `${entity}_disabled`;
    if (isLive) return `${entity}_live`;
    if (isPending) return `${entity}_pending`;
    return `${entity}_live`;
  }, []);

  const matchesMerchantEmail = React.useCallback((merchant, email) => {
    const cleanEmail = String(email || "")
      .trim()
      .toLowerCase();
    return [
      merchant?.user?.email,
      merchant?.email,
      merchant?.merchantEmail,
      merchant?.merchant_email,
      merchant?.businessEmail,
      merchant?.business_email,
      merchant?.contactEmail,
      merchant?.contact_email,
    ].some(
      (value) =>
        String(value || "")
          .trim()
          .toLowerCase() === cleanEmail,
    );
  }, []);

  const mergeMerchantRecords = React.useCallback((records = []) => {
    const merged = [];
    const seen = new Set();

    records.forEach((record) => {
      if (!record) return;
      const key =
        record?._id ||
        record?.id ||
        [
          record?.user?.email,
          record?.email,
          record?.merchantEmail,
          record?.businessEmail,
          record?.contactEmail,
        ].find(Boolean);

      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(record);
    });

    return merged;
  }, []);

  // Business fields — only shown when there is exactly 1 email entered
  // (Moved these into formik state for cleaner management and consistency)

  const fetchInvites = React.useCallback(async () => {
    try {
      const res = await axiosInstance.get("merchant/invitations?limit=2000");
      setAllInvites(res?.data?.data || []);
    } catch (e) {
      console.error("Failed to fetch invitations", e);
    }
  }, []);

  React.useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const checkMerchantRegistration = React.useCallback(
    async (email) => {
      if (!email) return "valid";
      const cleanE = email.trim().toLowerCase();
      try {
        // 0. Check member accounts first
        const memberRes = await axiosInstance.get(
          `admin/users?search=${cleanE}&limit=50`,
        );
        const members = memberRes?.data?.data || [];
        const member = members.find(
          (m) => (m?.email || "").toLowerCase() === cleanE,
        );
        if (member) {
          return classifyAccountStatus("member", member);
        }

        // 0.5. Check fundraiser accounts
        const fundraiserRes = await axiosInstance.get(
          `fundraiser/all?search=${cleanE}&limit=50`,
        );
        const fundraisers = fundraiserRes?.data?.data || [];
        const fundraiser = fundraisers.find(
          (f) =>
            (f?.user?.email || "").toLowerCase() === cleanE ||
            (f?.email || "").toLowerCase() === cleanE,
        );
        if (fundraiser) {
          return classifyAccountStatus("fundraiser", fundraiser);
        }

        // 1. Merchant profile is the source of truth for merchant status
        const [
          merchantSearchRes,
          deletedMerchantSearchRes,
          merchantContactRes,
          deletedMerchantContactRes,
        ] = await Promise.all([
          axiosInstance.get(`merchant?search=${cleanE}&limit=50`),
          axiosInstance.get(
            `merchant?search=${cleanE}&status=deleted&limit=50`,
          ),
          axiosInstance.get(`merchant?contactSearch=${cleanE}&limit=50`),
          axiosInstance.get(
            `merchant?contactSearch=${cleanE}&status=deleted&limit=50`,
          ),
        ]);

        const merchants = mergeMerchantRecords([
          ...(merchantSearchRes?.data?.data || []),
          ...(deletedMerchantSearchRes?.data?.data || []),
          ...(merchantContactRes?.data?.data || []),
          ...(deletedMerchantContactRes?.data?.data || []),
        ]);

        const merchant = merchants.find((record) =>
          matchesMerchantEmail(record, cleanE),
        );

        if (merchant) {
          return classifyAccountStatus("merchant", merchant);
        }

        // 2. Only fall back to invitation history if no account record exists
        const invitation = allInvites.find(
          (i) => i?.email?.toLowerCase() === cleanE,
        );

        if (invitation) {
          const iStatus = normalizeStatus(invitation?.status);
          const processedStatuses = [
            "joined",
            "registered",
            "approved",
            "completed",
            "accepted",
            "in-process",
            "in-progress",
          ];

          if (processedStatuses.includes(iStatus)) {
            return "already_invited";
          }

          return "valid";
        }

        return "valid";
      } catch (error) {
        console.error("checkMerchantRegistration error:", error);
        return "valid";
      }
    },
    [
      allInvites,
      classifyAccountStatus,
      matchesMerchantEmail,
      mergeMerchantRecords,
    ],
  );

  // Helper: render an inline status hint below a field based on merchant status
  const renderStatusHint = (status, emailArg = "", totalCount = 1) => {
    if (!status || status === "valid") return null;
    const displayEmail = emailArg && totalCount > 1 ? ` ${emailArg} ` : " ";
    const [entity = "account", state = ""] = String(status || "").split("_");
    const entityLabel =
      entity === "merchant"
        ? "Merchant"
        : entity === "fundraiser"
          ? "Fundraiser"
          : entity === "member"
            ? "Member"
            : "Account";

    if (status === "already_invited") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}already invited.
        </p>
      );
    }
    if (status === "member_live" || status === "member_registered") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}is already registered as a Member.
        </p>
      );
    }

    if (status === "member_disabled") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}belongs to a disabled Member account and
          cannot be invited.
        </p>
      );
    }

    if (status === "member_deleted") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}belongs to a deleted Member account and cannot
          be invited.
        </p>
      );
    }

    if (status === "fundraiser_live" || status === "fundraiser_registered") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}is already registered as a Fundraiser.
        </p>
      );
    }

    if (status === "fundraiser_disabled") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}belongs to a disabled Fundraiser account and
          cannot be invited.
        </p>
      );
    }

    if (status === "fundraiser_deleted") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}belongs to a deleted Fundraiser account and
          cannot be invited.
        </p>
      );
    }

    if (
      status === "merchant_live" ||
      status === "merchant_registered" ||
      status === "registered"
    ) {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}is already registered as a Merchant.
        </p>
      );
    }

    if (status === "merchant_disabled") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}belongs to a disabled Merchant account and
          cannot be invited.
        </p>
      );
    }

    if (status === "merchant_deleted" || status === "deleted_merchant") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}belongs to a deleted Merchant account and
          cannot be invited.
        </p>
      );
    }

    if (
      ["member_pending", "fundraiser_pending", "merchant_pending"].includes(
        status,
      ) ||
      status === "declined" ||
      status === "in-process"
    ) {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}belongs to an existing {entityLabel} account
          and cannot be invited right now.
        </p>
      );
    }

    if (state === "deleted" || state === "disabled") {
      return (
        <p
          key={emailArg}
          className="text-[#DC2626] text-[12px] font-poppins mt-1"
        >
          This email{displayEmail}belongs to an existing {entityLabel} account
          and cannot be invited.
        </p>
      );
    }

    // Any other existing account state
    return (
      <p
        key={emailArg}
        className="text-[#DC2626] text-[12px] font-poppins mt-1"
      >
        This email{displayEmail}already exists. Please try a different one.
      </p>
    );
  };

  const invitationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .required("Email is required")
          .test(
            "is-valid-email-list",
            "One or more email addresses are invalid",
            (value) => {
              if (!value) return true;
              const emails = value
                .split(",")
                .map((e) => e.trim())
                .filter(Boolean);
              return emails.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
            },
          )
          .test(
            "email-must-be-lowercase",
            "Email must be lowercase (e.g. test@gmail.com)",
            (value) => {
              if (!value) return true;
              const emails = value
                .split(",")
                .map((e) => e.trim())
                .filter(Boolean);
              return emails.every((e) => {
                const clean = String(e || "").trim();
                return clean === clean.toLowerCase();
              });
            },
          ),
        businessName: Yup.string().nullable(),
        businessNumber: Yup.string().nullable(),
      }),
    [],
  );

  // Re-validate form when emailStatus state updates
  // Moved this below formik initialization to avoid RefError

  // Check merchant status for the custom email field on Enter / blur
  const handleCustomEmailCheck = React.useCallback(
    async (val) => {
      const cleanEmail = (val || "").trim();
      if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
        setCustomEmailStatus(null);
        return;
      }
      setIsSearchLoading(true);
      try {
        const status = await checkMerchantRegistration(cleanEmail);
        setCustomEmailStatus(status);
      } finally {
        setIsSearchLoading(false);
      }
    },
    [checkMerchantRegistration],
  );

  const formik = useFormik({
    initialValues: {
      email: "",
      businessName: "",
      businessNumber: "",
      businessNumberCountry: "AU",
    },
    enableReinitialize: true,
    validationSchema: invitationSchema,
    onSubmit: async (values) => {
      try {
        // Determine current email list to decide if single (business fields apply)
        const emailList = (values?.email || "")
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean);
        const isSingle = !csvFile && emailList.length === 1;

        const payload = {
          emails: values?.email,
          ...(isSingle &&
            values?.businessName?.trim() && {
              businessName: values.businessName.trim(),
            }),
          ...(isSingle &&
            values?.businessNumber?.trim() && {
              businessNumber: values.businessNumber.trim(),
              businessNumberCountry: values.businessNumberCountry,
            }),
          ...(showCustomEmail && {
            body:
              emailBody +
              `
              <div style="font-family: 'Poppins', sans-serif; color: #535353; font-size: 13px; margin-top: 20px;">
                <p style="margin-bottom: 8px;">You can learn more at: <a href="https://swoodle.com.au/merchant-feature/" style="color: #00ADEF; text-decoration: underline;">https://swoodle.com.au/merchant-feature/</a></p>
                <p style="margin-bottom: 16px;">Or Book a Free Discovery Call 15 minute chat. No obligation: <a href="https://calendly.com/franco-swoodle" style="color: #00ADEF; text-decoration: underline;">https://calendly.com/franco-swoodle</a></p>
                <p style="color: #303030; font-weight: 500;">We're excited to have you join Swoodle and can't wait to see your first offer go live.</p>
              </div>
            `,
          }),
        };

        if (showCustomEmail) {
          payload.description = emailBody;
        }

        const invitePromise = await dispatch(sendAdminInvite(payload)).unwrap();
        const skippedEmails = Array.isArray(invitePromise?.data?.skippedEmails)
          ? invitePromise.data.skippedEmails
          : [];
        const sentCount = Number(invitePromise?.data?.sent || 0);

        if (invitePromise?.success === true) {
          if (sentCount > 0) {
            toast.success("Merchant invitation sent successfully!", {
              style: {
                background: "#E6F7FB",
                color: "#007EA7",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                padding: "12px 16px",
              },
              progressStyle: {
                background: "#007EA7",
              },
              icon: "✅",
            });
          }
          if (skippedEmails.length > 0) {
            const nextStatuses = {};
            skippedEmails.forEach((entry) => {
              const blockedEmail = String(entry?.email || "")
                .trim()
                .toLowerCase();
              if (!blockedEmail) return;
              const reason = String(entry?.reason || "").toLowerCase();
              if (reason.includes("deleted")) {
                nextStatuses[blockedEmail] = "merchant_deleted";
              } else if (reason.includes("disabled")) {
                nextStatuses[blockedEmail] = "merchant_disabled";
              } else {
                nextStatuses[blockedEmail] = "merchant_live";
              }
            });

            if (Object.keys(nextStatuses).length > 0) {
              setEmailStatus((prev) => ({ ...prev, ...nextStatuses }));
              if (showCustomEmail) {
                setCustomEmailStatus(Object.values(nextStatuses)[0]);
              }
            }
          }
          if (sentCount > 0) {
            // Reset form after successful submit
            formik.resetForm();
            navigate("/admin/invitation-list");
            fetchInvites(); // Refresh cache
          }
        } else {
          toast.error(invitePromise?.message || "Failed to send invitation");
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || "";
        const lowerMessage = errorMessage.toLowerCase();
        if (
          lowerMessage.includes("domain") ||
          lowerMessage.includes("email") ||
          lowerMessage.includes("provide valid email") ||
          lowerMessage.includes("invalid email")
        ) {
          formik.setFieldError("email", errorMessage);
          formik.setTouched({ ...formik.touched, email: true }, false);
        } else {
          toast.error(errorMessage || "An unexpected error occurred. Please try again.");
        }
      }
    },
  });

  // Re-validate form when emailStatus state updates
  React.useEffect(() => {
    if (formik.values.email) {
      formik.validateForm();
    }
  }, [emailStatus, formik.validateForm, formik.values.email]);

  // Debounced check for manual input field
  React.useEffect(() => {
    const cleanEmail = inputValue.trim();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchLoading(true);
      try {
        const status = await checkMerchantRegistration(cleanEmail);
        setEmailStatus((prev) => ({ ...prev, [cleanEmail]: status }));
      } finally {
        setIsSearchLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [inputValue, checkMerchantRegistration]);

  // Debounced check for custom email field
  React.useEffect(() => {
    if (!showCustomEmail) return;

    const cleanEmail = (formik.values.email || "").trim();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setCustomEmailStatus(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchLoading(true);
      try {
        const status = await checkMerchantRegistration(cleanEmail);
        setCustomEmailStatus(status);
      } finally {
        setIsSearchLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [formik.values.email, showCustomEmail, checkMerchantRegistration]);

  const toggleCustomEmail = (val) => {
    if (val) {
      if (csvFile) {
        handleClearCSV();
      }
      const currentEmails = email
        .split(",")
        .map((i) => i.trim())
        .filter(Boolean);
      if (currentEmails.length > 1) {
        formik.setFieldValue("email", currentEmails[0], true);
        toast.info(
          "Custom email template only supports a single recipient. Only the first email was kept.",
        );
      }
    }
    setShowCustomEmail(val);
  };

  const { email } = formik.values;
  const { handleChange, handleBlur, handleSubmit, errors, touched } = formik;

  // Derived: how many valid emails are currently in the form
  const currentEmailList = React.useMemo(() => {
    const source = csvFile
      ? csvFile.emails
      : (formik.values.email || "")
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean);
    return source;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.email, csvFile]);

  const isSingleEmail = !csvFile && currentEmailList.length === 1;

  const handleSampleDownload = () => {
    const csvContent =
      "Email,Business Name,Business Number\nmerchant1@example.com,Merchant One Pty Ltd,123456789\nmerchant2@example.com,Merchant Two Pty Ltd,987654321";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_merchant_invite.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleClearCSV = () => {
    setCsvFile(null);
    formik.resetForm();
    setInputValue("");
    const fileInput = document.getElementById("csvFileInput");
    if (fileInput) fileInput.value = "";
  };

  const handleManualEmailAdd = async (val) => {
    const cleanEmail = val.trim().replace(/,/g, "");
    if (!cleanEmail) return;

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      formik.setFieldError("email", "Invalid email format");
      return;
    }

    const currentEmails = formik.values.email
      ? formik.values.email
          .split(",")
          .map((i) => i.trim())
          .filter(Boolean)
      : [];

    if (showCustomEmail && currentEmails.length >= 1) {
      toast.info("Custom email template only supports a single recipient.");
      return;
    }

    if (currentEmails.includes(cleanEmail)) {
      formik.setFieldError("email", "Email already added");
      return;
    }

    const newEmails = [...currentEmails, cleanEmail];
    formik.setFieldValue("email", newEmails.join(", "), true);
    setInputValue("");

    setIsSearchLoading(true);
    try {
      const status = await checkMerchantRegistration(cleanEmail);
      setEmailStatus((prev) => ({ ...prev, ...{ [cleanEmail]: status } }));
    } finally {
      setIsSearchLoading(false);
    }

    // Touch the field immediately so errors are visible right after adding
    setTimeout(() => {
      formik.validateForm().then(() => {
        formik.setFieldTouched("email", true, false);
      });
    }, 100);
  };

  const handleManualEmailDelete = (tagToDelete) => {
    const currentEmails = formik.values.email
      .split(",")
      .map((i) => i.trim())
      .filter(Boolean);
    const newEmails = currentEmails.filter((email) => email !== tagToDelete);
    formik.setFieldValue("email", newEmails.join(", "), true);
  };

  const handleCSVUpload = () => {
    if (csvFile) {
      handleClearCSV();
    } else {
      document.getElementById("csvFileInput")?.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    // Reset form when CSV is uploaded (multi-merchant flow)
    formik.setFieldValue("businessName", "");
    formik.setFieldValue("businessNumber", "");
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        toast.error("Please upload a valid CSV file.");
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        try {
          // Parse CSV rows to extract email, businessName, businessNumber per row
          const lines = text.split(/\r?\n/).filter((l) => l.trim());
          let headerIndexes = {
            email: -1,
            businessName: -1,
            businessNumber: -1,
          };
          let csvRows = []; // { email, businessName, businessNumber }

          if (lines.length > 0) {
            // Try to detect a header row
            const firstLine = lines[0].toLowerCase();
            const hasHeader =
              firstLine.includes("email") || firstLine.includes("business");
            const headers = hasHeader
              ? lines[0].split(",").map((h) => h.trim().toLowerCase())
              : [];
            if (hasHeader) {
              headers.forEach((h, i) => {
                if (h.includes("email")) headerIndexes.email = i;
                if (h.includes("business name") || h === "businessname")
                  headerIndexes.businessName = i;
                if (h.includes("business number") || h === "businessnumber")
                  headerIndexes.businessNumber = i;
              });
            }
            const dataLines = hasHeader ? lines.slice(1) : lines;
            dataLines.forEach((line) => {
              const cols = line.split(",").map((c) => c.trim());
              const emailVal =
                headerIndexes.email >= 0 ? cols[headerIndexes.email] : cols[0];
              const emailMatch =
                emailVal &&
                emailVal.match(
                  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
                );
              if (emailMatch) {
                csvRows.push({
                  email: emailMatch[0].toLowerCase(),
                  businessName:
                    headerIndexes.businessName >= 0
                      ? cols[headerIndexes.businessName] || ""
                      : "",
                  businessNumber:
                    headerIndexes.businessNumber >= 0
                      ? cols[headerIndexes.businessNumber] || ""
                      : "",
                });
              }
            });
          }

          // Fallback: if no structured rows found, use old regex approach
          if (csvRows.length === 0) {
            const emailRegex =
              /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
            const foundRaw = text.match(emailRegex) || [];
            foundRaw.forEach((e) =>
              csvRows.push({
                email: e.trim().toLowerCase(),
                businessName: "",
                businessNumber: "",
              }),
            );
          }

          // Deduplicate by email
          const seen = new Set();
          csvRows = csvRows.filter((r) => {
            if (seen.has(r.email)) return false;
            seen.add(r.email);
            return true;
          });

          const foundEmails = csvRows.map((r) => r.email);
          const _unusedEmailRegex = null; // suppress lint for original variable name

          if (foundEmails && foundEmails.length > 0) {
            const uniqueEmails = foundEmails;
            const validationMap = {};

            uniqueEmails.forEach((email) => {
              const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
              validationMap[email] = isValid ? "pending" : "invalid";
            });

            formik.setFieldValue("email", uniqueEmails.join(", "), true);
            setCsvFile({
              name: file.name,
              count: uniqueEmails.length,
              emails: uniqueEmails,
              rows: csvRows, // full row data with businessName/businessNumber
              validation: validationMap,
            });

            setIsVerifying(true);

            const newStatuses = {};
            for (const email of uniqueEmails) {
              newStatuses[email] = await checkMerchantRegistration(email);
            }

            setEmailStatus((prev) => ({ ...prev, ...newStatuses }));
            setCsvFile((prev) => ({
              ...prev,
              validation: { ...prev.validation, ...newStatuses },
            }));
            setIsVerifying(false);

            setTimeout(() => {
              formik.validateForm();
              formik.setFieldTouched("email", true, true);
            }, 200);
          } else {
            formik.setFieldError("email", "No valid emails found in the CSV.");
            setIsVerifying(false);
          }
        } catch (error) {
          formik.setFieldError("email", "Failed to parse CSV file.");
          setIsVerifying(false);
        }
      };
      reader.readAsText(file);
      event.target.value = "";
    }
  };

  const handleDeleteEmail = (index) => {
    const updatedEmails = [...csvFile.emails];
    updatedEmails.splice(index, 1);

    if (updatedEmails.length === 0) {
      handleClearCSV();
      setIsPreviewOpen(false);
    } else {
      const updatedValidation = { ...csvFile.validation };
      delete updatedValidation[csvFile.emails[index]];
      setCsvFile({
        ...csvFile,
        count: updatedEmails.length,
        emails: updatedEmails,
        validation: updatedValidation,
      });
      formik.setFieldValue("email", updatedEmails.join(", "));
    }
  };

  const handleSaveEmail = async (index) => {
    if (!editedEmail.trim()) return;
    const updatedEmails = [...csvFile.emails];
    const oldEmail = updatedEmails[index];
    updatedEmails[index] = editedEmail;

    const newValidation = { ...csvFile.validation, [editedEmail]: "pending" };
    setCsvFile((prev) => ({
      ...prev,
      emails: updatedEmails,
      validation: newValidation,
    }));

    let status = "valid";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedEmail)) {
      status = "invalid";
    } else {
      status = await checkMerchantRegistration(editedEmail);
    }

    setCsvFile((prev) => {
      const updatedValidation = { ...prev.validation, [editedEmail]: status };
      if (oldEmail !== editedEmail) delete updatedValidation[oldEmail];
      return { ...prev, emails: updatedEmails, validation: updatedValidation };
    });
    formik.setFieldValue("email", updatedEmails.join(", "));
    setEditingRow(null);
  };

  const getStatusBadge = (email) => {
    const status = csvFile?.validation?.[email];
    if (!status || status === "pending")
      return <span className="text-[#8C8C8C] animate-pulse">Checking...</span>;
    switch (status.toLowerCase()) {
      case "valid":
        return (
          <span className="px-2 py-1 rounded-full bg-[#F0FDF4] text-[#15803D] text-[11px] font-[500] whitespace-nowrap">
            Valid
          </span>
        );
      case "invalid":
        return (
          <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">
            Invalid Format
          </span>
        );
      case "registered":
      case "merchant_registered":
      case "merchant_live":
      case "merchant_disabled":
      case "merchant_deleted":
      case "deleted_merchant":
        return (
          <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">
            {["merchant_deleted", "deleted_merchant"].includes(
              status.toLowerCase(),
            )
              ? "Deleted Merchant"
              : "Merchant"}
          </span>
        );
      case "member_registered":
      case "member_live":
      case "member_disabled":
      case "member_deleted":
        return (
          <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">
            {status.toLowerCase().includes("deleted")
              ? "Deleted Member"
              : "Member"}
          </span>
        );
      case "fundraiser_registered":
      case "fundraiser_live":
      case "fundraiser_disabled":
      case "fundraiser_deleted":
        return (
          <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">
            {status.toLowerCase().includes("deleted")
              ? "Deleted Fundraiser"
              : "Fundraiser"}
          </span>
        );
      case "declined":
        return (
          <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">
            Declined
          </span>
        );
      case "in-process":
        return (
          <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">
            In-Process
          </span>
        );
      case "already_invited":
        return (
          <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">
            Invited
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-[11px] font-[500] whitespace-nowrap">
            {status}
          </span>
        );
    }
  };

  return (
    <>
      {loading === true && <Loader />}

      <div className="bg-[#FFFFFF] min-h-screen py-6 sm:py-12 px-4 sm:px-8">
        <div className="max-w-[1400px] mx-auto">
          <div
            className={`flex flex-col ${showCustomEmail ? "lg:flex-row" : "items-center"} justify-center w-full gap-8 transition-all duration-500`}
          >
            <div
              className={`transition-all duration-500 w-full flex flex-col ${showCustomEmail ? "lg:w-[48%]" : "max-w-[650px]"}`}
            >
              <div className="w-full flex flex-col items-start mb-6">
                <h1 className="text-[28px] sm:text-[36px] font-[600] text-[#303030] mb-2 font-poppins text-left">
                  Let's Invite <span className="text-[#00ADEF]">Merchant!</span>
                </h1>
                <p className="text-[#767676] font-poppins text-[15px] sm:text-[17px] font-[400] text-left">
                  Invite merchants by entering their email addresses to join
                  Swoodle
                </p>
              </div>
              <div className="bg-white border border-[#E0E0E0] rounded-[15px] p-6 sm:p-8 shadow-sm flex flex-col gap-6 relative z-[30]">
                <div className="w-full">
                  <div className="flex flex-wrap items-end justify-between mb-2 gap-y-2">
                    <label className="block text-[#303030] bg-white text-[14px] font-[600] font-poppins">
                      {showCustomEmail ? "Merchant Email" : "Merchant Email(s)"}
                      <span className="text-red-500">*</span>
                    </label>
                    {!showCustomEmail && (
                      <button
                        type="button"
                        onClick={() => toggleCustomEmail(true)}
                        className="flex items-center gap-1.5 text-[#00ADEF] hover:text-[#0088CC] font-poppins text-[13px] font-[500] transition-colors"
                      >
                        <Type size={14} />
                        <span>Customise Email Template (Optional)</span>
                      </button>
                    )}
                  </div>
                  <div className="w-full relative">
                    {csvFile ? (
                      <div className="w-full h-[50px] border border-[#E0E0E0] rounded-[5px] bg-white flex items-center px-4 mb-0">
                        <div className="flex items-center gap-2 bg-white border border-[#00ADEF]/20 text-[#00ADEF] px-3 sm:px-4 py-1.5 rounded-full text-[12px] sm:text-[14px] font-[500] font-poppins shadow-sm">
                          <CsvIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate max-w-[200px] sm:max-w-[350px]">
                            {csvFile.name}
                          </span>
                          {isVerifying ? (
                            <div className="w-3.5 h-3.5 border-2 border-[#00ADEF] border-t-transparent rounded-full animate-spin ml-1"></div>
                          ) : (
                            <div
                              className="ml-1 p-0.5 hover:bg-[#00ADEF] hover:text-white rounded-full transition-colors cursor-pointer"
                              onClick={handleClearCSV}
                            >
                              <X size={12} />
                            </div>
                          )}
                        </div>
                        {isVerifying && (
                          <span className="ml-3 text-[12px] text-[#767676] font-poppins animate-pulse">
                            Verifying emails...
                          </span>
                        )}
                        {!isVerifying && (
                          <button
                            type="button"
                            onClick={() => setIsPreviewOpen(true)}
                            className="ml-auto text-[#00ADEF] text-[12px] font-[600] font-poppins hover:underline"
                          >
                            View Preview
                          </button>
                        )}
                      </div>
                    ) : showCustomEmail ? (
                      <div className="relative">
                        <input
                          type="text"
                          name="email"
                          className={`w-full h-[50px] border ${
                            (touched.email && errors.email) ||
                            (customEmailStatus &&
                              [
                                "registered",
                                "merchant_registered",
                                "merchant_live",
                                "merchant_disabled",
                                "merchant_deleted",
                                "merchant_pending",
                                "member_registered",
                                "member_live",
                                "member_disabled",
                                "member_deleted",
                                "member_pending",
                                "fundraiser_registered",
                                "fundraiser_live",
                                "fundraiser_disabled",
                                "fundraiser_deleted",
                                "fundraiser_pending",
                                "already_invited",
                                "declined",
                                "in-process",
                              ].includes(customEmailStatus))
                              ? "border-[#DC2626]"
                              : "border-[#E0E0E0]"
                          } rounded-[8px] px-4 outline-none font-poppins text-[14px] bg-[#FFFFFF] focus:border-[#00ADEF] transition-all`}
                          placeholder="Enter merchant email"
                          value={formik.values.email}
                          onChange={(e) => {
                            formik.handleChange(e);
                            setCustomEmailStatus(null);
                          }}
                          onBlur={(e) => {
                            formik.handleBlur(e);
                            handleCustomEmailCheck(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleCustomEmailCheck(e.target.value);
                            }
                          }}
                        />
                        {isSearchLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-[#00ADEF] border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`w-full min-h-[50px] border ${
                          (touched.email && errors.email) ||
                          (() => {
                            const BLOCKING = [
                              "registered",
                              "merchant_registered",
                              "merchant_live",
                              "merchant_disabled",
                              "merchant_deleted",
                              "merchant_pending",
                              "deleted_merchant",
                              "member_registered",
                              "member_live",
                              "member_disabled",
                              "member_deleted",
                              "member_pending",
                              "fundraiser_registered",
                              "fundraiser_live",
                              "fundraiser_disabled",
                              "fundraiser_deleted",
                              "fundraiser_pending",
                              "declined",
                              "in-process",
                            ];
                            return email
                              .split(",")
                              .map((i) => i.trim())
                              .filter(Boolean)
                              .some((e) => BLOCKING.includes(emailStatus[e]));
                          })()
                            ? "border-[#DC2626]"
                            : "border-[#E0E0E0]"
                        } rounded-[8px] bg-[#FFFFFF] flex flex-wrap items-center px-3 py-1 gap-2`}
                      >
                        {email
                          .split(",")
                          .map((i) => i.trim())
                          .filter(Boolean)
                          .map((tag, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 bg-[#00ADEF] text-white px-2 sm:px-3 py-1 rounded-[5px] text-[12px] sm:text-[13px] font-[500] shadow-sm"
                            >
                              <span>{tag}</span>
                              <div
                                className="cursor-pointer"
                                onClick={() => handleManualEmailDelete(tag)}
                              >
                                <X size={10} />
                              </div>
                            </div>
                          ))}
                        <div className="relative flex-1">
                          <input
                            className="w-full outline-none border-none min-w-[120px] h-[38px] text-[14px] font-poppins bg-transparent pr-8"
                            placeholder={email ? "" : "Enter merchant email(s)"}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "," || e.key === "Enter") {
                                e.preventDefault();
                                handleManualEmailAdd(inputValue);
                              }
                            }}
                            onBlur={() =>
                              inputValue && handleManualEmailAdd(inputValue)
                            }
                          />
                          {isSearchLoading &&
                            inputValue &&
                            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                              inputValue.trim(),
                            ) && (
                              <div className="absolute right-1 top-1/2 -translate-y-1/2">
                                <div className="w-3.5 h-3.5 border-2 border-[#00ADEF] border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                  {touched.email && errors.email && !csvFile && (
                    <p className="text-[#DC2626] text-[12px] font-poppins mt-1">
                      {errors.email}
                    </p>
                  )}
                  {/* Inline merchant status hints */}
                  {showCustomEmail &&
                    !errors.email &&
                    (isSearchLoading ? (
                      <p className="text-[#8C8C8C] text-[12px] font-poppins mt-1 animate-pulse">
                        Checking merchant status...
                      </p>
                    ) : (
                      renderStatusHint(
                        customEmailStatus,
                        formik.values.email,
                        1,
                      )
                    ))}
                  {!showCustomEmail &&
                    !csvFile &&
                    !errors.email &&
                    (() => {
                      const addedEmails = (formik.values.email || "")
                        .split(",")
                        .map((e) => e.trim())
                        .filter(Boolean);
                      const blockingStatuses = [
                        "registered",
                        "merchant_registered",
                        "merchant_live",
                        "merchant_disabled",
                        "merchant_deleted",
                        "merchant_pending",
                        "deleted_merchant",
                        "member_registered",
                        "member_live",
                        "member_disabled",
                        "member_deleted",
                        "member_pending",
                        "fundraiser_registered",
                        "fundraiser_live",
                        "fundraiser_disabled",
                        "fundraiser_deleted",
                        "fundraiser_pending",
                        "declined",
                        "in-process",
                        "already_invited",
                      ];
                      const existingEmails = addedEmails.filter((e) =>
                        blockingStatuses.includes(emailStatus[e]),
                      );

                      const typingEmail = inputValue.trim();
                      const isTypingValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                        typingEmail,
                      );

                      return (
                        <>
                          {isSearchLoading && isTypingValid && (
                            <p className="text-[#8C8C8C] text-[12px] font-poppins mt-1 animate-pulse">
                              Checking merchant status for {typingEmail}...
                            </p>
                          )}
                          {existingEmails.map((e) =>
                            renderStatusHint(
                              emailStatus[e],
                              e,
                              addedEmails.length,
                            ),
                          )}
                        </>
                      );
                    })()}
                  {!showCustomEmail && (
                    <p className="text-[#767676] text-[13px] font-poppins mt-2 flex items-start gap-1.5">
                      <InfoIcon className="w-4 h-4 text-[#00ADEF] flex-shrink-0 mt-0.5" />
                      <span className="leading-tight mt-[1px]">
                        Invite multiple merchants by separating their email
                        addresses with commas.
                      </span>
                    </p>
                  )}
                </div>

                {/* ── Business Name & Business Number (single-email only) ── */}
                {isSingleEmail && !csvFile && (
                  <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top duration-300 relative z-[20]">
                    <div className="w-full">
                      <Input
                        label={
                          <div className="flex items-center justify-start gap-1 w-full">
                            <span>Business Name</span>
                            <span className="text-[#8C8C8C] font-[400] text-[12px]">
                              (Optional)
                            </span>
                          </div>
                        }
                        name="businessName"
                        type="text"
                        value={formik.values.businessName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter Business Name"
                        labelClassName="text-[#303030] text-[14px] font-[600] font-poppins mb-2"
                        inputClass="w-full h-[50px] border border-[#E0E0E0] rounded-[8px] px-4 outline-none font-poppins text-[14px] bg-[#FFFFFF] focus:border-[#00ADEF] transition-all"
                      />
                    </div>
                    <div className="w-full">
                      <PhoneFormInput
                        label={
                          <div className="flex items-center justify-start gap-1 w-full">
                            <span>Business Number</span>
                            <span className="text-[#8C8C8C] font-[400] text-[12px]">
                              (Optional)
                            </span>
                          </div>
                        }
                        name="businessNumber"
                        country={formik.values.businessNumberCountry}
                        countryFieldName="businessNumberCountry"
                        value={formik.values.businessNumber}
                        setFieldValue={formik.setFieldValue}
                        onBlur={formik.handleBlur}
                        placeholder="Enter Business Number"
                        labelClass="text-[#303030] text-[14px] font-[600] font-poppins mb-2"
                        containerClass="!rounded-[8px]"
                        buttonClass="!rounded-l-[8px] !bg-[#D9D9D9] md:!w-[13%] sm:!w-[15%] lg:!w-[9%] xl:!w-[7%] 2xl:!w-[6%]"
                        inputClass="!h-[50px] !rounded-[8px] !bg-[#FFFFFF] !text-[#303030] !font-[400] !outline-none !font-poppins placeholder:!text-[#767676] placeholder:!opacity-100 placeholder:!font-poppins placeholder:!text-[14px]"
                        inputStyle={{
                          width: "100%",
                          border: "1px solid #E0E0E0",
                        }}
                        buttonStyle={{
                          border: "1px solid #E0E0E0",
                          borderRight: "none",
                        }}
                      />
                    </div>
                  </div>
                )}

                {showCustomEmail && (
                  <div className="w-full flex flex-col gap-6 animate-in fade-in slide-in-from-top duration-300">
                    <div className="flex items-center justify-between border-b pb-2">
                      <h3 className="text-[16px] font-[600] text-[#303030] font-poppins">
                        Customise Email Template
                      </h3>
                      <button
                        type="button"
                        onClick={() => toggleCustomEmail(false)}
                        className="text-[#767676] hover:text-[#DC2626] text-[12px] font-[500] flex items-center gap-1 transition-colors"
                      >
                        <X size={14} />
                        Discard Customisation
                      </button>
                    </div>
                    <div className="w-full">
                      <label className="block text-[#303030] text-[14px] font-[600] font-poppins mb-2">
                        Subject
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={emailSubject}
                          readOnly
                          className="w-full h-[50px] border border-[#E0E0E0] rounded-[8px] px-4 outline-none font-poppins text-[14px] bg-[#F5F5F5] cursor-not-allowed transition-all"
                        />
                      </div>
                    </div>
                    <div className="w-full">
                      <label className="block text-[#303030] text-[14px] font-[600] font-poppins mb-2">
                        Email Body
                      </label>
                      <ReactQuill
                        theme="snow"
                        value={emailBody}
                        onChange={setEmailBody}
                        modules={{ toolbar: [["bold", "italic", "underline"]] }}
                        className="w-full h-[280px] mb-12"
                      />
                    </div>
                  </div>
                )}

                {!showCustomEmail && (
                  <>
                    <div className="flex items-center justify-center w-full relative h-[20px] z-[10]">
                      <div className="h-[1px] bg-[#E0E0E0] w-full absolute z-0"></div>
                      <div className="bg-white px-4 z-10 text-[#767676] text-[13px] font-poppins">
                        Or
                      </div>
                    </div>

                    <div className="flex flex-row gap-3 sm:gap-4 w-full">
                      <button
                        type="button"
                        onClick={handleCSVUpload}
                        disabled={!!csvFile}
                        className={`flex-1 flex items-center justify-center gap-2 sm:gap-3 border border-[#E0E0E0] rounded-[8px] h-[45px] sm:h-[55px] ${csvFile ? "bg-[#F5F5F5] cursor-not-allowed" : "bg-white hover:bg-gray-50 hover:border-[#00ADEF] transition-all"}`}
                      >
                        <UploadIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#00ADEF]" />
                        <span className="font-poppins font-[600] text-[#00ADEF] text-[12px] sm:text-[14px] whitespace-nowrap">
                          {csvFile ? "CSV Uploaded" : "Upload CSV File"}
                        </span>
                        <input
                          type="file"
                          id="csvFileInput"
                          hidden
                          accept=".csv"
                          onChange={handleFileChange}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={handleSampleDownload}
                        className="flex-1 flex items-center justify-center gap-2 sm:gap-3 border border-[#E0E0E0] rounded-[8px] h-[45px] sm:h-[55px] bg-white hover:bg-gray-50 hover:border-[#767676] transition-all"
                      >
                        <CsvIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-[#303030] font-poppins font-[600] text-[12px] sm:text-[14px] whitespace-nowrap">
                          Sample File
                        </span>
                        <DownloadIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#767676]" />
                      </button>
                    </div>
                  </>
                )}

                <div className="w-full mt-2">
                  {/* Compute blocking statuses for the button */}
                  {(() => {
                    const BLOCKING = [
                      "registered",
                      "merchant_registered",
                      "merchant_live",
                      "merchant_disabled",
                      "merchant_deleted",
                      "merchant_pending",
                      "deleted_merchant",
                      "member_registered",
                      "member_live",
                      "member_disabled",
                      "member_deleted",
                      "member_pending",
                      "fundraiser_registered",
                      "fundraiser_live",
                      "fundraiser_disabled",
                      "fundraiser_deleted",
                      "fundraiser_pending",
                      "declined",
                      "in-process",
                    ];
                    const multiHasBlock =
                      !showCustomEmail &&
                      (formik.values.email || "")
                        .split(",")
                        .map((e) => e.trim())
                        .filter(Boolean)
                        .some((e) => BLOCKING.includes(emailStatus[e]));
                    const customHasBlock =
                      showCustomEmail && BLOCKING.includes(customEmailStatus);
                    const isHardBlocked = multiHasBlock || customHasBlock;
                    const emailHasUppercase = (() => {
                      const raw = String(formik.values.email || "");
                      const emails = raw
                        .split(",")
                        .map((e) => e.trim())
                        .filter(Boolean);
                      return emails.some((e) => {
                        const clean = String(e || "").trim();
                        return clean !== clean.toLowerCase();
                      });
                    })();

                    // Only show "Sending..." loader if actually submitting the form
                    const isRequesting = loading;

                    return (
                      <button
                        type="button"
                        disabled={
                          loading ||
                          isSearchLoading ||
                          isVerifying ||
                          isHardBlocked ||
                          emailHasUppercase
                        }
                        className="w-full bg-[#00ADEF] text-white rounded-[8px] py-3 text-[16px] font-[600] font-poppins shadow-md hover:bg-[#0096d1] transition-all disabled:opacity-50 disabled:cursor-not-allowed h-[60px] flex items-center justify-center"
                        onClick={async () => {
                          await formik.setFieldTouched("email", true, true);

                          if (showCustomEmail) {
                            const emailVal = formik.values.email?.trim();
                            if (!emailVal) {
                              formik.setFieldError(
                                "email",
                                "Email is required",
                              );
                              return;
                            }
                            {
                              const clean = String(emailVal || "").trim();
                              if (clean && clean !== clean.toLowerCase()) {
                                formik.setFieldError(
                                  "email",
                                  "Email must be lowercase (e.g. test@gmail.com)",
                                );
                                return;
                              }
                            }
                            let statusToCheck = customEmailStatus;
                            if (!statusToCheck) {
                              setIsSearchLoading(true);
                              statusToCheck =
                                await checkMerchantRegistration(emailVal);
                              setCustomEmailStatus(statusToCheck);
                              setIsSearchLoading(false);
                            }
                            if (BLOCKING.includes(statusToCheck)) return;
                          }

                          const emailVal = formik.values.email;
                          const emails =
                            emailVal
                              ?.split(",")
                              ?.map((e) => e.trim())
                              ?.filter(Boolean) || [];

                          if (emails.length === 0 && !csvFile) {
                            formik.setFieldError("email", "Email is required");
                            return;
                          }
                          if (
                            emails.some((e) => {
                              const clean = String(e || "").trim();
                              return clean !== clean.toLowerCase();
                            })
                          ) {
                            formik.setFieldError(
                              "email",
                              "Email must be lowercase (e.g. test@gmail.com)",
                            );
                            return;
                          }

                          // Ensure all emails have their status checked
                          let updatedEmailStatus = { ...emailStatus };
                          let needsRecheck = false;

                          if (!csvFile) {
                            for (const e of emails) {
                              if (!emailStatus[e]) {
                                needsRecheck = true;
                                break;
                              }
                            }
                          }

                          if (needsRecheck) {
                            setIsSearchLoading(true);
                            for (const e of emails) {
                              if (!emailStatus[e]) {
                                const status =
                                  await checkMerchantRegistration(e);
                                updatedEmailStatus[e] = status;
                              }
                            }
                            setEmailStatus(updatedEmailStatus);
                            setIsSearchLoading(false);

                            // Re-check blocking after status update
                            const multiHasBlock = emails.some((e) =>
                              BLOCKING.includes(updatedEmailStatus[e]),
                            );
                            if (multiHasBlock) return;
                          }

                          const errors = await formik.validateForm();
                          if (Object.keys(errors).length > 0) return;
                          formik.handleSubmit();
                        }}
                      >
                        {isRequesting ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Sending...</span>
                          </div>
                        ) : (
                          "Send Invitation"
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>

            {showCustomEmail && (
              <div className="w-full lg:w-[50%] animate-in fade-in slide-in-from-right duration-500 lg:sticky lg:top-4 z-[10]">
                <div className="w-full flex flex-col items-start gap-4">
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <Eye size={20} className="text-[#00ADEF]" />
                      <h3 className="text-[18px] font-[600] text-[#303030] font-poppins">
                        Email Preview
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 bg-[#F5F5F5] rounded-full p-1 border border-[#E0E0E0]">
                      <button
                        type="button"
                        onClick={() => setPreviewMode("desktop")}
                        className={`p-2 rounded-full transition-all duration-300 transform ${previewMode === "desktop" ? "bg-[#00ADEF] text-white scale-110 shadow-md" : "text-[#767676] hover:bg-gray-200"}`}
                      >
                        <Monitor size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewMode("mobile")}
                        className={`p-2 rounded-full transition-all duration-300 transform ${previewMode === "mobile" ? "bg-[#00ADEF] text-white scale-110 shadow-md" : "text-[#767676] hover:bg-gray-200"}`}
                      >
                        <Smartphone size={18} />
                      </button>
                    </div>
                  </div>

                  <div
                    className={`w-full transition-all duration-700 ease-in-out flex justify-center ${previewMode === "mobile" ? "py-4" : ""}`}
                  >
                    <div
                      className="transition-all duration-[800ms] ease-in-out relative flex flex-col overflow-hidden"
                      style={{
                        width: previewMode === "mobile" ? "320px" : "100%",
                        height: previewMode === "mobile" ? "660px" : "auto",
                        maxHeight:
                          previewMode === "mobile" ? "660px" : "2000px",
                        borderWidth: previewMode === "mobile" ? "10px" : "1px",
                        borderColor:
                          previewMode === "mobile" ? "#1a1a1b" : "#E0E0E0",
                        borderRadius:
                          previewMode === "mobile" ? "50px" : "24px",
                        padding: previewMode === "mobile" ? "0px" : "16px",
                        backgroundColor:
                          previewMode === "mobile" ? "#FFFFFF" : "#F9F9F9",
                        transitionProperty:
                          "width, height, max-height, border, border-radius, padding, background-color",
                      }}
                    >
                      {/* Dynamic Island for Mobile */}
                      <div
                        className={`absolute top-4 left-1/2 -translate-x-1/2 h-7 bg-[#1a1a1b] rounded-full z-20 transition-all duration-500 ease-in-out ${previewMode === "mobile" ? "w-32 opacity-100 scale-100" : "w-0 opacity-0 scale-50"}`}
                      ></div>

                      <div
                        className={`w-full bg-white flex flex-col font-poppins transition-all duration-[800ms] delay-100 ease-out ${
                          previewMode === "mobile"
                            ? "h-full overflow-y-auto pt-14 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:display-none translate-y-0 opacity-100"
                            : "shadow-sm rounded-[5px] translate-y-4 opacity-100"
                        }`}
                      >
                        <div className="p-6 pb-2 flex justify-start">
                          <h2
                            className={`text-[20px] sm:text-[24px] font-[600] text-[#303030] font-poppins transition-all duration-500 ${previewMode === "mobile" ? "whitespace-nowrap scale-95 origin-left" : ""}`}
                          >
                            Welcome to{" "}
                            <span className="text-[#00ADEF]">Swoodle</span>
                          </h2>
                        </div>
                        <div className="p-6 pt-0 flex flex-col gap-5">
                          <div className="w-full flex items-center justify-start p-0 mb-2">
                            <img
                              src={
                                previewMode === "mobile"
                                  ? InviteMerchantInfographicMobile
                                  : InviteMerchantInfographic
                              }
                              alt="Swoodle Invitation"
                              className={`${previewMode === "mobile" ? "w-[95%]" : "w-[98%]"} h-auto rounded-[0px] ml-0 transition-all duration-700 ease-in-out`}
                            />
                          </div>
                          <div
                            className="text-[#535353] text-[13px] leading-[1.6] text-left"
                            dangerouslySetInnerHTML={{ __html: emailBody }}
                          ></div>

                          <div className="w-full flex justify-start pl-2">
                            <button
                              type="button"
                              className="w-max px-8 bg-[#00ADEF] text-white py-3 rounded-full font-[600] text-[15px] shadow-sm hover:bg-[#0096d1] transition-all"
                            >
                              Set Up Your Account
                            </button>
                          </div>

                          <div className="flex flex-col gap-2 text-[12px] text-[#535353] text-left mt-2">
                            <p>
                              You can learn more at:{" "}
                              <span className="text-[#00ADEF] underline cursor-pointer">
                                https://swoodle.com.au/merchant-feature/
                              </span>
                            </p>
                            <p>
                              Or Book a Free Discovery Call 15 minute chat. No
                              obligation:{" "}
                              <span className="text-[#00ADEF] underline cursor-pointer">
                                https://calendly.com/franco-swoodle
                              </span>
                            </p>
                          </div>

                          <p className="text-[13px] text-[#303030] font-[500] text-left">
                            We're excited to have you join Swoodle and can't
                            wait to see your first offer go live.
                          </p>

                          <div className="bg-[#F2F4F5] rounded-[24px] p-6 text-left flex flex-col gap-2">
                            <div className="flex flex-col">
                              <span
                                className={`${previewMode === "mobile" ? "text-[15px]" : "text-[16px]"} font-[600] text-[#303030]`}
                              >
                                Warm Regards,
                              </span>
                              <span
                                className={`${previewMode === "mobile" ? "text-[15px]" : "text-[16px]"} font-[600] text-[#303030]`}
                              >
                                The Swoodle Team
                              </span>
                            </div>
                            <p
                              className={`${previewMode === "mobile" ? "text-[12px]" : "text-[13px]"} text-[#535353] border-t border-white/40 pt-1.5`}
                            >
                              P.S. Sign-up only takes a few minutes — you'll be
                              ready to start in no time!
                            </p>
                          </div>
                        </div>

                        <div
                          className={`w-full bg-[#242856] text-white p-6 sm:p-8 font-poppins ${previewMode === "mobile" ? "rounded-none" : "rounded-[24px]"}`}
                        >
                          <div
                            className={`flex flex-col ${previewMode === "mobile" ? "items-start text-left" : "md:flex-row justify-between items-start"} gap-4`}
                          >
                            {/* Footers Socials for Mobile - Left Aligned Top */}
                            {previewMode === "mobile" && (
                              <div className="flex flex-col items-start gap-3">
                                <div className="flex gap-2">
                                  <FaInstagram
                                    size={16}
                                    className="text-[#FFC943] hover:opacity-80"
                                  />
                                  <FaFacebookF
                                    size={16}
                                    className="text-[#FFC943] hover:opacity-80"
                                  />
                                  <FaYoutube
                                    size={16}
                                    className="text-[#FFC943] hover:opacity-80"
                                  />
                                  <FaLinkedinIn
                                    size={16}
                                    className="text-[#FFC943] hover:opacity-80"
                                  />
                                  <FaTiktok
                                    size={16}
                                    className="text-[#FFC943] hover:opacity-80"
                                  />
                                </div>
                                <div className="flex items-center gap-1.5 text-[10px] text-white/60 font-[500]">
                                  <span className="hover:text-white cursor-pointer">
                                    Contact us
                                  </span>
                                  <span className="h-2 w-[1px] bg-white/20"></span>
                                  <span className="hover:text-white cursor-pointer">
                                    Privacy Policy
                                  </span>
                                  <span className="h-2 w-[1px] bg-white/20"></span>
                                  <span className="hover:text-white cursor-pointer">
                                    T&C's
                                  </span>
                                </div>
                              </div>
                            )}

                            <div
                              className={`flex flex-col ${previewMode === "mobile" ? "items-start" : "items-start"} gap-4`}
                            >
                              <SwoodleLogo className="h-[25px] w-auto" />
                              <p className="text-[11px] text-white/80 leading-relaxed max-w-[280px]">
                                Grow your business, reach new customers.
                                Fundraise with ease, and unlock exclusive deals
                                and savings.
                              </p>
                              <div
                                className={`flex flex-col gap-2 mt-2 ${previewMode === "mobile" ? "items-start" : ""}`}
                              >
                                <div className="flex items-center gap-3 text-[11px]">
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="#FFC943"
                                  >
                                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21 11.72 11.72 0 003.67 1.17 1 1 0 01.88 1v3.09a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.09a1 1 0 011 .88 11.72 11.72 0 001.17 3.67 1 1 0 01-.21 1.11l-2.43 2.13z" />
                                  </svg>
                                  <span>0401 811 115</span>
                                </div>
                                <div className="flex items-center gap-3 text-[11px]">
                                  <img
                                    src={EnvelopeIcon}
                                    width="16"
                                    height="16"
                                    alt="email"
                                  />
                                  <span>info@swoodle.com.au</span>
                                </div>
                                <div
                                  className={`flex items-start gap-3 text-[11px] ${previewMode === "mobile" ? "text-left justify-start" : "text-left"}`}
                                >
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="#FFC943"
                                    className="flex-shrink-0 mt-0.5"
                                  >
                                    <path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
                                  </svg>
                                  <div
                                    className={`flex text-left ${previewMode === "mobile" ? "flex-col" : "flex-row"}`}
                                  >
                                    <span>
                                      105a Glenferrie Rd Malvern, Victoria,{" "}
                                      {previewMode !== "mobile" &&
                                        "3144, Australia"}
                                    </span>
                                    {previewMode === "mobile" && (
                                      <span>3144, Australia</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Footer Right side for Desktop only */}
                            {previewMode !== "mobile" && (
                              <div className="flex flex-col items-start md:items-end gap-3 mt-1">
                                <div className="flex gap-1">
                                  <FaInstagram
                                    size={15}
                                    className="text-[#FFC943] hover:opacity-80 cursor-pointer"
                                  />
                                  <FaFacebookF
                                    size={15}
                                    className="text-[#FFC943] hover:opacity-80 cursor-pointer"
                                  />
                                  <FaYoutube
                                    size={15}
                                    className="text-[#FFC943] hover:opacity-80 cursor-pointer"
                                  />
                                  <FaLinkedinIn
                                    size={15}
                                    className="text-[#FFC943] hover:opacity-80 cursor-pointer"
                                  />
                                  <FaTiktok
                                    size={15}
                                    className="text-[#FFC943] hover:opacity-80 cursor-pointer"
                                  />
                                </div>
                                <div className="flex items-center gap-0.5 text-[10px] text-white/60 font-[500] whitespace-nowrap">
                                  <span className="hover:text-white cursor-pointer px-0.5">
                                    Contact us
                                  </span>
                                  <span className="h-3 w-[1px] bg-white/20"></span>
                                  <span className="hover:text-white cursor-pointer px-0.5">
                                    Privacy Policy
                                  </span>
                                  <span className="h-3 w-[1px] bg-white/20"></span>
                                  <span className="hover:text-white cursor-pointer px-0.5">
                                    T&C's
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="w-full h-[1px] bg-white/5 mb-4"></div>
                          <div className="text-[10px] text-center text-white/30">
                            © {new Date().getFullYear()} Swoodle. All rights
                            reserved.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isPreviewOpen && (
        <DetailsModal
          handleClose={() => setIsPreviewOpen(false)}
          popupClassName="!w-[800px]"
          content={
            <div className="w-full flex flex-col items-start gap-4 p-2">
              <h3 className="text-[20px] font-[600] text-[#00ADEF] font-poppins">
                {csvFile?.name}
              </h3>
              <div className="relative w-full max-h-[400px] overflow-y-auto border border-[#E0E0E0] rounded-[8px]">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-[#F5F5F5] sticky top-0">
                    <tr>
                      <th className="p-3 text-[13px] font-[600] font-poppins border-b border-[#E0E0E0]">
                        #
                      </th>
                      <th className="p-3 text-[13px] font-[600] font-poppins border-b border-[#E0E0E0]">
                        Email Address
                      </th>
                      <th className="p-3 text-[13px] font-[600] font-poppins border-b border-[#E0E0E0]">
                        Business Name
                      </th>
                      <th className="p-3 text-[13px] font-[600] font-poppins border-b border-[#E0E0E0]">
                        Business Number
                      </th>
                      <th className="p-3 text-[13px] font-[600] font-poppins border-b border-[#E0E0E0] w-[130px]">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvFile?.emails?.map((email, index) => {
                      const rowData =
                        csvFile?.rows?.find((r) => r.email === email) || {};
                      return (
                        <tr
                          key={index}
                          className="hover:bg-[#F9F9F9] border-b border-[#E0E0E0] last:border-0 group"
                        >
                          <td className="p-3 text-[13px] font-[400] text-[#767676] font-poppins w-[40px]">
                            {index + 1}
                          </td>
                          <td className="p-3 text-[13px] font-[400] text-[#767676] font-poppins relative">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate max-w-[180px]">
                                {email}
                              </span>
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                <button
                                  onClick={() => handleDeleteEmail(index)}
                                  className="p-1 hover:bg-red-50 rounded text-gray-500 hover:text-red-500"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-[13px] font-[400] text-[#303030] font-poppins">
                            {rowData.businessName || (
                              <span className="text-[#C0C0C0] italic">—</span>
                            )}
                          </td>
                          <td className="p-3 text-[13px] font-[400] text-[#303030] font-poppins">
                            {rowData.businessNumber || (
                              <span className="text-[#C0C0C0] italic">—</span>
                            )}
                          </td>
                          <td className="p-3 text-[13px] font-[400] font-poppins">
                            {getStatusBadge(email)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          }
        />
      )}
    </>
  );
};

export default InvitaionFormPage;
